import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ArticlesFilter, ArticlesService } from '../../../service/articles.service';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {BehaviorSubject, forkJoin, Observable, Subscription, take} from 'rxjs';
import { Offer, OfferArticle } from '../../../models/offer.model';
import { LazyLoadEvent, MenuItem, MessageService } from 'primeng/api';
import { Article } from '../../../models/article.model';
import { Customer } from '../../../models/customer.model';
import { ActivatedRoute, Router } from '@angular/router';
import { CodelistsService } from '../../../service/codelists.service';
import { CustomersService } from '../../../service/customers.service';
import { ProjectsService } from '../../../service/projects.service';
import { PricesService } from '../../../service/prices.service';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { OrdersService } from '../../../service/orders.service';
import {tap} from 'rxjs/operators';

@Component({
    selector: 'app-order-full-modal',
    templateUrl: './order-full-modal.component.html',
    styleUrls: ['./order-full-modal.component.scss'],
    standalone: false
})
export class OrderFullModalComponent implements OnInit, OnDestroy {

  loading = true;
  filter: ArticlesFilter = new ArticlesFilter(0, 5, 'name');
  form: UntypedFormGroup;
  buyersForm: UntypedFormGroup;
  receiverForm: UntypedFormGroup;
  sub: Subscription;
  filtered: any;
  stepsItems: MenuItem[];
  activeIndex = 0;
  order: Offer;
  parities: any[];
  private saving: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private items: BehaviorSubject<Article[]> = new BehaviorSubject([]);
  items$: Observable<Article[]> = this.items.asObservable();
  private count: BehaviorSubject<number> = new BehaviorSubject(0);
  count$: Observable<number> = this.count.asObservable();
  private pending: BehaviorSubject<boolean> = new BehaviorSubject(false);
  pending$: Observable<boolean> = this.pending.asObservable();

  @Input() buyer: Customer;

  constructor(
    private ordersService: OrdersService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private codelistsService: CodelistsService,
    private customersService: CustomersService,
    private articlesService: ArticlesService,
    private projectsService: ProjectsService,
    public pricesService: PricesService,
    private modal: DialogModule,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private translate: TranslateService,
    fb: UntypedFormBuilder
  ) {
    this.form = fb.group({
      id: fb.control(null),
      number: fb.control(null),
      dateCreated: fb.control(new Date(), Validators.required),
      dateValid: fb.control(new Date(), Validators.required),
      dateDelivery: fb.control(new Date(), Validators.required),
      regNumber: fb.control(null),
      remarks: fb.control(null),
      seller: null,
      buyer: this.buyersForm,
      receiver: this.receiverForm,
      ordersArticles: fb.array([]),
      discount: fb.control(0, Validators.required),
      discountArticles: fb.control(null),
      priceNet: fb.control(null),
      priceGross: fb.control(null),
      priceTotal: fb.control(null),
      vatCost: fb.control(null),
      project: fb.control(null),
      parity: fb.control(null),
      daysToDelivery: fb.control(null, Validators.required),
    });
  }

  get ordersArticlesFormArray() {
    return this.form.controls.ordersArticles as UntypedFormArray;
  }

  ngOnInit(): void {
    this.stepsItems = [
      {
        label: this.translate.instant('CUSTOMER.CUSTOMER'),
        command: (event: any) => {
          this.activeIndex = 0;
        }
      },
      {
        label: this.translate.instant('MENU.ARTICLES'),
        command: (event: any) => {
          this.activeIndex = 1;
        }
      },
      {
        label: this.translate.instant('OFFER.COMMERCIAL_TERMS&CONDITIONS'),
        command: (event: any) => {
          this.activeIndex = 2;
        }
      }
    ];
    if (this.config && this.config.data) {
      if (this.config.data.offer) {
        this.order = this.config.data.offer;
      } else {
        if (this.config.data.buyer) {
          this.fetchCustomer(this.config.data.buyer);
        }
        if (this.config.data.articles) {
          this.config.data.articles.forEach(article => {
            this.addArticleOnOrder(article);
          });
        }
      }
    }
    if (this.order && this.order.id) {
      this.form.patchValue(this.order);

      // Fill the articles
      if (this.order.ordersArticles.length !== 0) {
        this.order.ordersArticles.forEach(offerArticle => {
          this.addOrderArticleOnOrder(offerArticle);
        });
      }
      // Create new date from existing offer to get rid of error
      this.form.get('dateCreated').setValue(new Date(this.order.dateCreated));
      this.form.get('dateValid').setValue(new Date(this.order.dateValid));
      this.form.get('dateDelivery').setValue(new Date(this.order.dateDelivery));
    } else {
      // Create new date from existing offer to get rid of error
      this.form.get('dateCreated').setValue(new Date());
      this.form.get('dateValid').setValue(new Date());
      this.form.get('dateDelivery').setValue(new Date());
      this.addSellerToOrder();
    }
  }

  ngOnDestroy(): void {
    this.saving.complete();
    this.pending.complete();
    this.items.complete();
    this.count.complete();
  }

  fetchArticles(): void {
    // If using lazy load and loading MUST use timeout... https://github.com/primefaces/primeng/issues/3042
    setTimeout(() => {
      this.loading = true;
      this.pending.next(true);
      this.sub = this.articlesService.findWithPrice(this.filter).subscribe(data => {
          this.pending.next(false);
          this.items.next(data.values);
          this.count.next(data.count);
          this.loading = false;
        },
        error => {
          console.log(error);
        });
    }, 0);
  }

  fetchCustomer(id: string): void {
    this.loading = true;
    this.customersService.get(id)
      .subscribe(
        data => {
          this.form.get('buyer').setValue(data);
        },
        error => {
          console.log(error);
        });
  }

  pageChanged(event: LazyLoadEvent): void {
    this.filter.limit = event.rows;
    this.filter.offset = event.first;
    this.filter.search = event.globalFilter;
    if (event.sortOrder === -1) {
      this.filter.orderBy = '-' + event.sortField;
    } else {
      this.filter.orderBy = event.sortField;
    }
    this.fetchArticles();
  }

  onRowEditSave(offerArticle: OfferArticle, index: number): void {
    if (offerArticle.quantity > 0) {
      // TODO: WTF to do here !!!
      offerArticle.article.priceTotal = this.pricesService.articlePriceTotal(offerArticle);
      offerArticle.priceTotal = this.pricesService.articlePriceTotal(offerArticle);
      this.ordersArticlesFormArray.at(index).patchValue(offerArticle);
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const order = {...this.order, ...this.form.getRawValue()};
    if (!order.receiver) {
      order.receiver = order.buyer;
    }
    order.priceNet = this.pricesService.offerPriceWithoutDiscount(this.form.getRawValue());
    order.discountArticles = this.pricesService.articlesPriceDiscount(this.form.getRawValue());
    order.vatCost = this.pricesService.offerPriceVat(this.form.getRawValue());
    if (order.id) {
      this.ordersService.update(order.id, order)
        .subscribe(
          response => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Successfully updated order: ' + order.number,
            });
            this.ref.close(order);
            this.router.navigate(['order/' + response.id]);
          },
          error => {
            console.log(error);
          });
    } else {
      this.ordersService.create(order)
        .subscribe(
          response => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Successfully created order: ' + response.number,
            });
            this.ref.close(order);
            this.router.navigate(['order/' + response.id]);
          },
          error => {
            console.log(error);
          });
    }
  }

  filterCustomers(event): void {
    let filtered: any[] = [];
    const query = event.query;
    // a request to a remote url with the query and return filtered results
    this.customersService.findByName(query)
      .subscribe(
        data => {
          if (data != null) {
            filtered = data;
          }
          this.filtered = filtered;
        },
        error => {
          console.log(error);
        });
  }

  filterProjects(event): void {
    let filtered: any[] = [];
    const query = event.query;
    // a request to a remote url with the query and return filtered results
    this.projectsService.findByName(query)
      .subscribe(
        data => {
          if (data != null) {
            filtered = data;
          }
          this.filtered = filtered;
        },
        error => {
          console.log(error);
        });
  }

  filterCodelist(event, codelist): void {
    let filtered: any[] = [];
    const query = event.query;
    // a request to a remote url with the query and return filtered results
    this.codelistsService.findByName(query, codelist)
      .subscribe(
        data => {
          if (data != null) {
            filtered = data;
          }
          this.filtered = filtered;
        },
        error => {
          console.log(error);
        });
  }

  /*  Articles on form  */

  addSellerToOrder(): void {

    const seller = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      registrationNumber: new UntypedFormControl(null),
      name: new UntypedFormControl('Chemcolor Sevnica d.o.o.'),
      nameLong: new UntypedFormControl('Chemcolor Sevnica d.o.o.'),
      url: new UntypedFormControl(null),
      email: new UntypedFormControl(null),
      phone: new UntypedFormControl(null),
      mobile: new UntypedFormControl(null),
      fax: new UntypedFormControl(null),
      vat: new UntypedFormControl(null),
      company: new UntypedFormControl(true),
      contacts: new UntypedFormArray([]),
      address: new UntypedFormGroup({
        id: new UntypedFormControl(null),
        street1: new UntypedFormControl('Dolnje Brezovo 35'),
        street2: new UntypedFormControl(null),
        city: new UntypedFormControl('Blanca'),
        postal: new UntypedFormControl('Blanca'),
        postalCode: new UntypedFormControl('8283'),
        stateOrProvince: new UntypedFormControl(null),
        country: new UntypedFormGroup({
          id: new UntypedFormControl(15000196),
          name: new UntypedFormControl('Slovenia'),
          description: new UntypedFormControl(null),
          code2: new UntypedFormControl('SI'),
          code3: new UntypedFormControl('SVN'),
          vat1: new UntypedFormControl(22),
          vat2: new UntypedFormControl(9.5),
        }),
      }),
    });
    this.form.controls.seller.setValue(seller.getRawValue());
  }

  addOrderArticleOnOrder(offerArticle: OfferArticle): void {
    const offerArticleForm = new UntypedFormGroup({
      id: new UntypedFormControl(offerArticle.id),
      article: new UntypedFormControl(offerArticle.article),
      order: new UntypedFormControl(null),
      priceNet: new UntypedFormControl(offerArticle.priceNet),
      priceGross: new UntypedFormControl(offerArticle.priceNet),
      priceList: new UntypedFormControl(offerArticle.priceList),
      discount: new UntypedFormControl(offerArticle.discount),
      discountList: new UntypedFormControl(offerArticle.discountList),
      priceTotal: new UntypedFormControl(offerArticle.priceTotal),
      quantity: new UntypedFormControl(offerArticle.quantity),
      vat1: new UntypedFormControl(this.form.getRawValue().seller.address.country.vat1),
      vat2: new UntypedFormControl(this.form.getRawValue().seller.address.country.vat2),
    });

    this.ordersArticlesFormArray.push(offerArticleForm);
  }

  addArticleOnOrder(article: Article): void {
    let articleExists = false;
    this.ordersArticlesFormArray.controls.forEach(value => {
      if (value.get('article').value.id === article.id) {
        articleExists = true;
      }
    });

    if (!articleExists) {
      article.quantity = 1;
      article.discount = 0;
      const offerArticleForm = new UntypedFormGroup({
        id: new UntypedFormControl(null),
        article: new UntypedFormControl(article),
        priceNet: new UntypedFormControl(article.priceList.price),
        priceGross: new UntypedFormControl(article.priceGross),
        priceList: new UntypedFormControl(article.priceList),
        discount: new UntypedFormControl(article.discount),
        discountList: new UntypedFormControl(article.discountList),
        priceTotal: new UntypedFormControl(article.priceTotal),
        quantity: new UntypedFormControl(article.quantity),
        vat1: new UntypedFormControl(this.form.getRawValue().seller.address.country.vat1),
        vat2: new UntypedFormControl(this.form.getRawValue().seller.address.country.vat2),
      });

      this.ordersArticlesFormArray.push(offerArticleForm);
    } else {
      this.messageService.add({severity: 'info', summary: this.translate.instant('ORDER.ARTICLE_ALREADY_ADDED')});
    }
  }

  updatePricesOnOfferArticles(): void {
    const articleIds = this.ordersArticlesFormArray.controls
      .map(control => (control.get('article').value as Article).id);

    if (articleIds.length === 0) { return; }

    const requests$ = articleIds.map(id =>
      this.articlesService.findWithPriceById(id, this.filter.customerId).pipe(
        tap(updatedArticle => {
          const control = this.ordersArticlesFormArray.controls.find(
            c => (c.get('article').value as Article).id === updatedArticle.id
          );
          if (control) {
            // Update only the price instead of the entire article
            control.get('priceNet').setValue(updatedArticle.priceList.price);

            // Get current quantity and discount
            const quantity = control.get('quantity').value;
            const discount = control.get('discount').value;

            // Calculate total price if quantity exists
            if (quantity !== null) {
              let totalPrice = updatedArticle.priceList.price * quantity;

              // Apply discount if it exists
              if (discount !== null) {
                totalPrice = totalPrice * (1 - discount / 100); // assuming discount is in percentage
              }

              // Update the total price
              control.get('priceTotal').setValue(totalPrice);

            }
          }
        })
      )
    );

    forkJoin(requests$).pipe(
      take(1)
    ).subscribe({
      next: (updatedArticles) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('ACTIONS.SUCCESS'),
          detail: this.translate.instant('ARTICLES.PRICES_UPDATED', { count: updatedArticles.length })
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ACTIONS.ERROR'),
          detail: this.translate.instant('ARTICLES.PRICES_UPDATE_ERROR')
        });
        console.error('Error updating articles:', error);
      }
    });
  }

  onRowDelete(offerArticle: OfferArticle, index: number): void {
    this.ordersArticlesFormArray.removeAt(index);
  }

  selectBuyer(event: any): void {
    this.filter.customerId = event.value.id;
    this.fetchArticles();
    this.updatePricesOnOfferArticles();
  }
}
